import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, ShoppingCart, Receipt, BookOpen, MessageSquare, LogOut, Bell, Menu, X, Shield } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = 'https://customer-assets.emergentagent.com/job_inventory-core-11/artifacts/43j2lsb0_Tirth-Logo-Final-File-01.png';

const NAV = [
  { path: '/portal',          label: 'Overview',     icon: Home, end: true },
  { path: '/portal/orders',   label: 'Orders',       icon: ShoppingCart },
  { path: '/portal/place-order', label: 'New Order', icon: ShoppingCart },
  { path: '/portal/bills',    label: 'Bills',        icon: Receipt },
  { path: '/portal/ledger',   label: 'Ledger',       icon: BookOpen },
  { path: '/portal/notes',    label: 'Account Notes', icon: MessageSquare },
];

export default function ClientPortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (user?.role === 'client') {
      axios.get(`${API}/portal/me`, { withCredentials: true })
        .then(r => setMe(r.data)).catch(() => {});
    }
  }, [user]);

  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'client') return <Navigate to="/dashboard" replace />;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      <Toaster richColors theme="dark" position="top-right" />
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-zinc-950/90 backdrop-blur border-b border-zinc-800">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button className="md:hidden p-1 text-zinc-400" onClick={() => setMobileOpen(v => !v)} data-testid="portal-menu-toggle">
              {mobileOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
            <img src={LOGO} alt="Tirth" className="w-9 h-9 rounded-lg" />
            <div>
              <div className="text-zinc-50 font-heading font-semibold text-sm leading-tight">Tirth Industries</div>
              <div className="text-zinc-500 text-[10px] flex items-center gap-1"><Shield size={9} />Client Portal</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-zinc-50 text-xs font-semibold">{me?.client?.name || user.name}</div>
              <div className="text-zinc-500 text-[10px]">{user.email}</div>
            </div>
            <button onClick={handleLogout} data-testid="portal-logout-btn"
              className="p-2 rounded-lg text-zinc-400 hover:text-red-300 hover:bg-red-500/10">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-5 flex gap-6">
        {/* Sidebar (desktop) + Drawer (mobile) */}
        <aside className={`${mobileOpen ? 'fixed inset-0 z-40 bg-zinc-950/95 p-6 pt-24' : 'hidden'} md:block md:static md:p-0 md:w-52 md:flex-shrink-0`}>
          <nav className="flex flex-col gap-1">
            {NAV.map(item => (
              <NavLink key={item.path} to={item.path} end={item.end}
                onClick={() => setMobileOpen(false)}
                data-testid={`portal-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                  isActive ? 'bg-amber-500/15 text-amber-300 border border-amber-500/30' : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900'
                }`}>
                <item.icon size={14} /> {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0">
          <Outlet context={{ me, reload: () => axios.get(`${API}/portal/me`, { withCredentials: true }).then(r => setMe(r.data)) }} />
        </main>
      </div>
    </div>
  );
}
